const yup = require('yup');

// A number field stays a *string* in form state. `type="number"` was deliberately not used:
// it hands back a locale-normalised value with a "." decimal point, which would change the
// submitted payload and the DealerDesk XML for a field editors can switch on and off at
// will. So the user's own notation is preserved and only validated here.
//
// Accepts "1250", "1250,5", "1250.5", "1.250,50", "150.000" and "1 250,50".
//
// Matched as whole shapes rather than normalised separator by separator: stripping every dot
// once a comma appeared also accepted "1.250,50.5" and read it as 1250.505, so a typo could
// reach the lead email as a plausible-looking number.
const GROUPED = /^-?\d{1,3}(\.\d{3})+(,\d+)?$/;
const DECIMAL_COMMA = /^-?\d+(,\d+)?$/;
const DECIMAL_DOT = /^-?\d+(\.\d+)?$/;

function isEmpty(value) {
  return value === null || value === undefined || value === '';
}

/**
 * Parses an editor-facing German number into a JS number, or null when it is not one.
 *
 * The dot is the ambiguous one, so the whole input is matched against one of three shapes:
 *   1. digits grouped in threes by dots, with an optional comma decimal — "150.000" is a
 *      mileage, "1.250,50" a price
 *   2. a plain number with a comma decimal ("1250,5")
 *   3. a plain number with a dot decimal ("1250.5"), which is what a numeric keypad produces
 *
 * Anything else is rejected, mixtures such as "1.250,50.5" included.
 *
 * Shape 1 reads "0.500" as 500 rather than 0.5. That is the intended German reading; anyone
 * meaning a half writes "0,5".
 */
function parseNumber(value) {
  const raw = String(value).trim().replace(/\s/g, '');

  if (GROUPED.test(raw)) {
    return Number(raw.replace(/\./g, '').replace(',', '.'));
  }

  if (DECIMAL_COMMA.test(raw)) {
    return Number(raw.replace(',', '.'));
  }

  if (DECIMAL_DOT.test(raw)) {
    return Number(raw);
  }

  return null;
}

function deserializeValue(state) {
  if (Array.isArray(state.value)) {
    if (state.value.length > 0) return state.value[0];
    return '';
  }

  return state.value;
}

/**
 * The length cap every variant keeps, so switching a field's input type can never make an
 * answer that already fits stop fitting.
 */
function withLengthCap(schema, isMultiline) {
  return schema.max(
    isMultiline ? 4000 : 255,
    isMultiline ? 'Maximal 4000 Zeichen möglich.' : 'Maximal 255 Zeichen möglich.',
  );
}

function numberSchema({ minValue, maxValue }) {
  let schema = yup
    .string()
    .nullable()
    .test(
      'is-number',
      'Bitte geben Sie eine Zahl ein.',
      (value) => isEmpty(value) || parseNumber(value) !== null,
    );

  // Bounds are checked against the parsed value, so "1.250" is not read as 1.25 — and an
  // unparseable entry is left to `is-number` rather than failing twice.
  if (minValue !== null && minValue !== undefined) {
    schema = schema.test(
      'min-value',
      `Bitte geben Sie einen Wert von mindestens ${minValue} ein.`,
      (value) => {
        const parsed = isEmpty(value) ? null : parseNumber(value);
        return parsed === null || parsed >= minValue;
      },
    );
  }

  if (maxValue !== null && maxValue !== undefined) {
    schema = schema.test(
      'max-value',
      `Bitte geben Sie einen Wert von höchstens ${maxValue} ein.`,
      (value) => {
        const parsed = isEmpty(value) ? null : parseNumber(value);
        return parsed === null || parsed <= maxValue;
      },
    );
  }

  return schema;
}

// Deliberately loose: international numbers, extensions and the "0521 / 123 45-67" house
// style all have to pass. It only rejects what cannot be dialled at all.
const PHONE_PATTERN = /^[+()/\d\s.-]{5,}$/;

/**
 * `isMultiline` wins over `inputType`: a textarea cannot carry an input type, and a field
 * left on the default still validates exactly as it did before this option existed.
 */
function validationSchema({
  isMultiline,
  inputType,
  minValue,
  maxValue,
}) {
  if (isMultiline || !inputType || inputType === 'text') {
    return withLengthCap(yup.string().nullable(), isMultiline);
  }

  if (inputType === 'number') {
    return withLengthCap(numberSchema({ minValue, maxValue }), false);
  }

  if (inputType === 'email') {
    return withLengthCap(
      yup.string().nullable().email('Bitte geben Sie eine gültige E-Mail-Adresse ein.'),
      false,
    );
  }

  if (inputType === 'tel') {
    return withLengthCap(
      yup.string().nullable().matches(PHONE_PATTERN, {
        message: 'Bitte geben Sie eine gültige Telefonnummer ein.',
        excludeEmptyString: true,
      }),
      false,
    );
  }

  // An input type the CMS knows but this build does not: validate as plain text rather than
  // throwing the whole form away, the same way `SelectField` treats an unmapped variant.
  return withLengthCap(yup.string().nullable(), false);
}

module.exports = {
  getDefaultValue: () => '',
  serializeValue: (value) => ({ value }),
  deserializeValue,
  getDisplayValue: (...args) => deserializeValue(...args),
  validationSchema,
};
